/**
 * Audio Manager - Howler.js Wrapper
 *
 * Optimized audio for games with:
 * - Sound pooling for frequently played sounds
 * - Volume control per category
 * - Spatial audio support
 * - Preloading
 */

import { Howl, Howler, SoundSpriteDefinitions } from 'howler';

export type AudioCategory = 'master' | 'sfx' | 'music' | 'ui' | 'voice';

export interface SoundConfig {
  src: string | string[];
  volume?: number;
  loop?: boolean;
  category?: AudioCategory;
  pool?: number;  // Pool size for frequently played sounds
  preload?: boolean;
  sprite?: SoundSpriteDefinitions;
}

interface SoundInstance {
  howl: Howl;
  category: AudioCategory;
}

export class AudioManager {
  private sounds: Map<string, SoundInstance> = new Map();
  private volumes: Map<AudioCategory, number> = new Map([
    ['master', 1],
    ['sfx', 1],
    ['music', 1],
    ['ui', 1],
    ['voice', 1],
  ]);

  private muted = false;
  private musicId: string | null = null;
  private currentMusicHowlId: number | null = null;

  constructor() {
    // Configure Howler
    Howler.autoUnlock = true;
    Howler.html5PoolSize = 10;
  }

  /**
   * Register a sound
   */
  register(id: string, config: SoundConfig): void {
    const category = config.category ?? 'sfx';
    const volume = (config.volume ?? 1) * this.getVolume(category);

    const howl = new Howl({
      src: Array.isArray(config.src) ? config.src : [config.src],
      volume,
      loop: config.loop ?? false,
      preload: config.preload ?? true,
      pool: config.pool ?? 5,
      sprite: config.sprite,
    });

    this.sounds.set(id, { howl, category });
  }

  /**
   * Preload multiple sounds
   */
  preload(configs: Record<string, SoundConfig>): Promise<void> {
    const promises: Promise<void>[] = [];

    for (const [id, config] of Object.entries(configs)) {
      this.register(id, config);

      const sound = this.sounds.get(id);
      if (sound) {
        promises.push(
          new Promise((resolve, reject) => {
            sound.howl.once('load', () => resolve());
            sound.howl.once('loaderror', (_, err) => reject(err));
          })
        );
      }
    }

    return Promise.all(promises).then(() => {});
  }

  /**
   * Play a sound
   */
  play(id: string, sprite?: string): number | null {
    const sound = this.sounds.get(id);
    if (!sound) {
      console.warn(`Sound "${id}" not found`);
      return null;
    }

    // Update volume before playing
    const volume = this.getVolume(sound.category);
    sound.howl.volume(volume);

    return sound.howl.play(sprite);
  }

  /**
   * Play a sound with specific settings
   */
  playWith(
    id: string,
    options: {
      volume?: number;
      rate?: number;
      loop?: boolean;
      sprite?: string;
    }
  ): number | null {
    const sound = this.sounds.get(id);
    if (!sound) return null;

    const howlId = sound.howl.play(options.sprite);

    if (options.volume !== undefined) {
      const finalVolume = options.volume * this.getVolume(sound.category);
      sound.howl.volume(finalVolume, howlId);
    }

    if (options.rate !== undefined) {
      sound.howl.rate(options.rate, howlId);
    }

    if (options.loop !== undefined) {
      sound.howl.loop(options.loop, howlId);
    }

    return howlId;
  }

  /**
   * Stop a sound
   */
  stop(id: string, howlId?: number): void {
    const sound = this.sounds.get(id);
    if (!sound) return;

    if (howlId !== undefined) {
      sound.howl.stop(howlId);
    } else {
      sound.howl.stop();
    }
  }

  /**
   * Pause a sound
   */
  pause(id: string, howlId?: number): void {
    const sound = this.sounds.get(id);
    if (!sound) return;

    if (howlId !== undefined) {
      sound.howl.pause(howlId);
    } else {
      sound.howl.pause();
    }
  }

  /**
   * Resume a sound
   */
  resume(id: string, howlId?: number): void {
    const sound = this.sounds.get(id);
    if (!sound) return;

    if (howlId !== undefined) {
      sound.howl.play(howlId);
    } else {
      sound.howl.play();
    }
  }

  /**
   * Fade a sound
   */
  fade(id: string, from: number, to: number, duration: number, howlId?: number): void {
    const sound = this.sounds.get(id);
    if (!sound) return;

    const categoryVolume = this.getVolume(sound.category);
    sound.howl.fade(from * categoryVolume, to * categoryVolume, duration, howlId);
  }

  /**
   * Play music (stops current music)
   */
  playMusic(id: string, fadeIn = 1000): void {
    // Fade out current music
    if (this.musicId && this.currentMusicHowlId !== null) {
      this.fade(this.musicId, 1, 0, fadeIn, this.currentMusicHowlId);
      const oldId = this.musicId;
      const oldHowlId = this.currentMusicHowlId;
      setTimeout(() => this.stop(oldId, oldHowlId), fadeIn);
    }

    // Start new music
    const sound = this.sounds.get(id);
    if (!sound) {
      console.warn(`Music "${id}" not found`);
      return;
    }

    sound.howl.loop(true);
    sound.howl.volume(0);
    this.currentMusicHowlId = sound.howl.play();
    this.musicId = id;

    // Fade in
    this.fade(id, 0, 1, fadeIn, this.currentMusicHowlId);
  }

  /**
   * Stop music
   */
  stopMusic(fadeOut = 1000): void {
    if (this.musicId && this.currentMusicHowlId !== null) {
      this.fade(this.musicId, 1, 0, fadeOut, this.currentMusicHowlId);
      const id = this.musicId;
      const howlId = this.currentMusicHowlId;
      setTimeout(() => this.stop(id, howlId), fadeOut);
      this.musicId = null;
      this.currentMusicHowlId = null;
    }
  }

  /**
   * Set category volume
   */
  setVolume(category: AudioCategory, volume: number): void {
    this.volumes.set(category, Math.max(0, Math.min(1, volume)));

    // Update all sounds in this category
    for (const [, sound] of this.sounds) {
      if (sound.category === category || category === 'master') {
        const finalVolume = this.getVolume(sound.category);
        sound.howl.volume(finalVolume);
      }
    }
  }

  /**
   * Get effective volume for a category
   */
  getVolume(category: AudioCategory): number {
    const masterVolume = this.volumes.get('master') ?? 1;
    const categoryVolume = this.volumes.get(category) ?? 1;
    return this.muted ? 0 : masterVolume * categoryVolume;
  }

  /**
   * Mute all audio
   */
  mute(): void {
    this.muted = true;
    Howler.mute(true);
  }

  /**
   * Unmute all audio
   */
  unmute(): void {
    this.muted = false;
    Howler.mute(false);
  }

  /**
   * Toggle mute
   */
  toggleMute(): boolean {
    if (this.muted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.muted;
  }

  /**
   * Check if muted
   */
  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Unload a sound
   */
  unload(id: string): void {
    const sound = this.sounds.get(id);
    if (sound) {
      sound.howl.unload();
      this.sounds.delete(id);
    }
  }

  /**
   * Unload all sounds
   */
  dispose(): void {
    for (const [, sound] of this.sounds) {
      sound.howl.unload();
    }
    this.sounds.clear();
    this.musicId = null;
    this.currentMusicHowlId = null;
  }
}
